import { useEffect, useState } from "react"

import * as d3 from "d3"
import { useMotionValueEvent, useSpring } from "motion/react"

import { numberFmt } from "@/util/numberFmt.ts"

import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip"

export default function MultiGauge({
  thresholds,
  colors,
  value,
  labels,
  placeholder = "—",
}: {
  thresholds: number[]
  colors: string[]
  /**
   * The value to display. Pass `null` or `undefined` when data isn't
   * available (loading / error): the needle will hold its last position
   * instead of animating back to 0, and the label will render `placeholder`
   * instead of a misleading "0".
   */
  value: number | null | undefined
  labels: boolean
  /** Label shown when `value` is null/undefined. Defaults to "—". */
  placeholder?: string
}) {
  const motionVal = useSpring(0)
  const [displayVal, setDisplayVal] = useState(0)

  const hasValue = value != null

  useEffect(() => {
    if (hasValue) {
      motionVal.set(value as number)
    }
    // When value is nullish, intentionally don't update the spring so the
    // needle stays put instead of snapping back to 0 on load/error.
  }, [value, hasValue, motionVal])

  useMotionValueEvent(motionVal, "change", (latest) => {
    setDisplayVal(latest)
  })

  const data = thresholds.map((t, i) => (i == 0 ? t : t - thresholds[i - 1]))
  const max = thresholds[thresholds.length - 1]

  const ratio = (displayVal > 0 ? Math.max(max - displayVal, 0) : max) / max

  const radius = 100
  const oR = 100
  const iR = 65
  const origin = {
    x: 150,
    y: 110,
  }

  const needleBaseRadius = 6

  const endPoint = {
    x: Math.cos(Math.PI * ratio) * radius + origin.x,
    y: origin.y - Math.sin(Math.PI * ratio) * radius,
  }
  const labelPoint = {
    x: Math.cos(Math.PI * ratio) * (radius + 25) + origin.x,
    y: origin.y - Math.sin(Math.PI * ratio) * (radius + 15),
  }
  const p1 = {
    x: Math.cos(Math.PI * ratio - Math.PI / 2) * needleBaseRadius + origin.x,
    y: origin.y - Math.sin(Math.PI * ratio - Math.PI / 2) * needleBaseRadius,
  }
  const p2 = {
    x: Math.cos(Math.PI * ratio + Math.PI / 2) * needleBaseRadius + origin.x,
    y: origin.y - Math.sin(Math.PI * ratio + Math.PI / 2) * needleBaseRadius,
  }

  const arc = d3.arc()
  const pie = d3.pie().padAngle(0).sort(null).startAngle(0).endAngle(Math.PI)
  const arcs = pie(data).map((slice) => {
    return arc({
      innerRadius: iR,
      outerRadius: oR,
      startAngle: slice.startAngle,
      endAngle: slice.endAngle,
    })
  })

  return (
    <div className="max-w-[375px] sm:max-w-[450px] w-full grow flex justify-center items-center">
      <svg className="w-full h-auto" viewBox="-10 -20 320 165" preserveAspectRatio="xMidYMid meet">
        <g transform={`translate(${origin.x}, ${origin.y}) rotate(-90 0 0)`}>
          {arcs.map((slice, i) => {
            return (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <path key={i} d={slice || ""} fill={colors[i]} />
                </TooltipTrigger>
                <TooltipContent>
                  {i == 0
                    ? `0 - ${numberFmt(thresholds[0])}`
                    : `${numberFmt(thresholds[i - 1])} - ${numberFmt(thresholds[i])}`}
                </TooltipContent>
              </Tooltip>
            )
          })}
        </g>
        <g>
          <circle
            r={needleBaseRadius}
            cx={origin.x}
            cy={origin.y}
            strokeWidth={1}
            stroke="var(--chart-4)"
            fill="var(--chart-4)"
          />
          <path
            fill="var(--chart-4)"
            d={`M${endPoint.x} ${endPoint.y} L${p1.x} ${p1.y} L${p2.x} ${p2.y} L${endPoint.x} ${endPoint.y}`}
            stroke="var(--chart-4)"
            strokeWidth={1}
          />
          {labels && (
            <text
              x={labelPoint.x}
              y={labelPoint.y}
              fill="var(--foreground)"
              style={{ fontWeight: "bold" }}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {hasValue ? numberFmt(displayVal) : placeholder}
            </text>
          )}
        </g>
        {labels && (
          <g>
            <text
              x={origin.x - (radius - 15)}
              y={origin.y + 20}
              fill="var(--foreground)"
              textAnchor="middle"
            >
              0
            </text>
            <text
              x={origin.x + (radius - 15)}
              y={origin.y + 20}
              textAnchor="middle"
              fill="var(--foreground)"
            >
              {numberFmt(max)}
            </text>
          </g>
        )}
      </svg>
    </div>
  )
}
